/**
 * PriceHistoryChart — the ONE price-history visualisation used wherever IziSell
 * shows observed prices over time.
 *
 * Why this exists
 * ---------------
 * Two different charts had grown up: the Product Analyzer's rich `MarketHistory`
 * and a primitive SVG inside `OpportunityDetailDialog` that rendered a plain path
 * for 2+ points and — worse — a TEXT CARD for exactly one observation. So the
 * first real observation, the moment a customer most wants to see "we checked
 * this", produced no chart at all.
 *
 * Design rules (correctness rules, not styling):
 *   * ONE observation draws a real visible marker at its real timestamp. It never
 *     draws a line, because a line between one point and nothing is a fabricated
 *     trend.
 *   * No synthetic points are ever appended: no zero, no "today", no repeat of the
 *     current price to pad the axis, no interpolation across unobserved periods.
 *   * A missing value is a GAP. The line breaks across it, so an unobserved period
 *     can never look like an observed unchanged price.
 *   * Trend language only appears with 2+ REAL observations of that series, and
 *     always carries both observation dates.
 *   * Coverage is stated from real data, so a one-day dataset is never described
 *     as a 180-day history.
 *
 * Pure SVG with a viewBox: scales on mobile, needs no chart dependency.
 */
import React, { useMemo, useState } from "react";

const DEFAULT_SERIES = [
  { key: "walmart", label: "Walmart price", color: "#0071dc" },
  { key: "source", label: "Source price", color: "#e0459a" },
];

const money = (v) =>
  v == null || !Number.isFinite(Number(v)) ? "—" : `$${Number(v).toFixed(2)}`;

// The year is included on purpose: a history that spans a year boundary is
// ambiguous without it, and an observation date is evidence.
const fmtTs = (ms) =>
  new Date(ms).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const fmtDay = (ms) =>
  new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function numOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalise many input shapes into `[{ at, values: {seriesKey} }]`.
 * Accepts the dialog snapshot rows (`observed_at`/`source_price`/`walmart_price`)
 * and single-series observation rows (`price` + `series`), so callers do not
 * reshape data first.
 */
export function normalizeHistoryPoints(rows) {
  const points = [];
  for (const row of rows || []) {
    if (!row || typeof row !== "object") continue;
    const rawAt = row.observed_at ?? row.observedAt ?? row.at;
    if (!rawAt) continue;
    const ms = new Date(rawAt).getTime();
    if (!Number.isFinite(ms)) continue;

    const values = {};
    if ("walmart_price" in row || "source_price" in row) {
      values.walmart = numOrNull(row.walmart_price);
      values.source = numOrNull(row.source_price);
    }
    if (row.price != null) {
      const key =
        row.series === "source" || row.subject_type === "source_product"
          ? "source"
          : "walmart";
      values[key] = numOrNull(row.price);
    }
    if (row.values && typeof row.values === "object") {
      Object.assign(values, row.values);
    }
    points.push({ at: ms, values });
  }
  points.sort((a, b) => a.at - b.at);
  return points;
}

/** Observations carrying a value for this series. */
export function seriesObservations(points, key) {
  return (points || []).filter((p) => p.values && p.values[key] != null);
}

/** Real coverage for ONE series. Never counts interpolated points. */
export function seriesStats(points, key) {
  const obs = seriesObservations(points, key);
  if (!obs.length) return null;
  const values = obs.map((p) => p.values[key]);
  const first = obs[0];
  const last = obs[obs.length - 1];
  const change = values.length >= 2 ? last.values[key] - first.values[key] : null;
  const pct =
    values.length >= 2 && first.values[key] > 0
      ? (change / first.values[key]) * 100
      : null;
  return {
    observations: obs.length,
    first,
    last,
    low: Math.min(...values),
    high: Math.max(...values),
    change,
    pct,
    spanMs: last.at - first.at,
  };
}

function SeriesLegend({ series, stats }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-muted mb-2">
      {series.map((s) => {
        const st = stats[s.key];
        return (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <i className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
            <span>{s.label}</span>
            {st ? (
              <span className="tabular-nums text-ink-primary font-semibold">
                {money(st.last.values[s.key])}
              </span>
            ) : (
              <span className="italic">not observed</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export function PriceHistoryChart({
  rows = [],
  // Server-computed coverage (first/last/checks/change/range + available windows).
  // When present it is authoritative: the server counts real observation times,
  // which is more reliable than inferring from chart rows.
  coverageMeta = null,
  series = DEFAULT_SERIES,
  loading = false,
  error = null,
  onRetry = null,
  height = 180,
  className,
  testId = "price-history-chart",
}) {
  const [hover, setHover] = useState(null);
  const points = useMemo(() => normalizeHistoryPoints(rows), [rows]);
  const stats = useMemo(
    () => Object.fromEntries(series.map((s) => [s.key, seriesStats(points, s.key)])),
    [points, series]
  );

  if (loading) {
    return (
      <div
        className="border border-edge-subtle rounded-[10px] p-4 text-compact text-ink-muted"
        data-testid={`${testId}-loading`}
      >
        Loading price history…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="border border-amber-200 bg-amber-50 rounded-[10px] p-4 text-compact"
        data-testid={`${testId}-error`}
      >
        <div className="text-amber-800 font-semibold">Could not load price history.</div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-[12px] font-semibold text-signal-blue hover:underline"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  const observedSeries = series.filter((s) => stats[s.key]);
  if (!points.length || !observedSeries.length) {
    return (
      <div
        className="border border-dashed border-edge-subtle rounded-[10px] p-4 bg-surface-2 text-compact text-ink-muted"
        data-testid={`${testId}-empty`}
      >
        <div className="font-semibold text-ink-primary mb-1">
          Price history not yet available
        </div>
        <div>
          No price observation has been recorded for this product yet. The chart
          appears as soon as a genuine observation is captured — nothing is
          estimated to fill the gap.
        </div>
      </div>
    );
  }

  const W = 720;
  const P = { t: 12, r: 16, b: 26, l: 52 };
  const H = height;
  const allValues = observedSeries.flatMap((s) =>
    seriesObservations(points, s.key).map((p) => p.values[s.key])
  );
  let lo = Math.min(...allValues);
  let hi = Math.max(...allValues);
  if (lo === hi) {
    // Pad around the real value rather than anchoring at zero, which would imply
    // fabricated variation.
    const pad = Math.max(1, Math.abs(lo) * 0.15);
    lo -= pad;
    hi += pad;
  }
  const t0 = points[0].at;
  const t1 = points[points.length - 1].at;
  const span = t1 - t0;
  const X = (ms) =>
    span > 0 ? P.l + ((ms - t0) / span) * (W - P.l - P.r) : P.l + (W - P.l - P.r) / 2;
  const Y = (v) => P.t + ((hi - v) / (hi - lo)) * (H - P.t - P.b);

  /** Break the path at gaps so an unobserved period never looks flat. */
  function pathFor(key) {
    let d = "";
    let pen = false;
    for (const p of points) {
      const v = p.values[key];
      if (v == null) {
        pen = false;
        continue;
      }
      d += `${pen ? "L" : "M"}${X(p.at).toFixed(1)},${Y(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  }

  const singleTimestamp = points.length === 1;
  const pointCount = observedSeries.reduce((n, s) => n + stats[s.key].observations, 0);

  return (
    <div className={className} data-testid={testId}>
      <SeriesLegend series={series} stats={stats} />

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full block"
          style={{ height: H }}
          role="img"
          aria-label={`Price history with ${pointCount} observation${pointCount === 1 ? "" : "s"}`}
          data-testid={`${testId}-svg`}
        >
          {[hi, (hi + lo) / 2, lo].map((v, i) => (
            <g key={i}>
              <line x1={P.l} x2={W - P.r} y1={Y(v)} y2={Y(v)} stroke="#e5e7eb" strokeWidth="1" />
              <text x={P.l - 6} y={Y(v) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">
                {money(v)}
              </text>
            </g>
          ))}

          <text x={P.l} y={H - 8} fontSize="9" fill="#9ca3af">
            {fmtDay(t0)}
          </text>
          {span > 0 ? (
            <text x={W - P.r} y={H - 8} fontSize="9" fill="#9ca3af" textAnchor="end">
              {fmtDay(t1)}
            </text>
          ) : null}

          {/* Lines only when there is real movement to connect. */}
          {!singleTimestamp &&
            observedSeries.map((s) => (
              <path
                key={`line-${s.key}`}
                d={pathFor(s.key)}
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                data-testid={`${testId}-line-${s.key}`}
              />
            ))}

          {/* EVERY real observation gets a visible marker, including the first. */}
          {observedSeries.map((s) =>
            seriesObservations(points, s.key).map((p, i) => (
              <circle
                key={`${s.key}-${p.at}-${i}`}
                cx={X(p.at)}
                cy={Y(p.values[s.key])}
                r={hover && hover.point === p ? 5 : 3.5}
                fill="#fff"
                stroke={s.color}
                strokeWidth="2"
                data-testid={`${testId}-point-${s.key}`}
                onMouseEnter={() => setHover({ point: p, series: s })}
                onMouseLeave={() => setHover(null)}
              />
            ))
          )}
        </svg>

        {hover && hover.point ? (
          <div
            className="absolute top-0 right-0 rounded-md bg-navy-900 text-white text-[11px] px-2 py-1 pointer-events-none"
            data-testid={`${testId}-tooltip`}
          >
            <div className="font-semibold">{fmtTs(hover.point.at)}</div>
            <div>
              {hover.series.label}: {money(hover.point.values[hover.series.key])}
            </div>
          </div>
        ) : null}
      </div>

      <CoverageNote
        points={points}
        series={series}
        stats={stats}
        coverageMeta={coverageMeta}
      />
    </div>
  );
}

/**
 * States what the data supports. This is what stops a single reading being
 * described as a trend or a 180-day history.
 */
function CoverageNote({ points, series, stats, coverageMeta = null }) {
  const observed = series.filter((s) => stats[s.key]);
  // Count distinct CHECK TIMES, not values. One snapshot holding a Walmart and a
  // source price is ONE price check, not two observations — summing per-series
  // counts double-counted it and implied more coverage than exists.
  const checks = new Set(points.map((p) => p.at)).size;
  const spanDays = points.length
    ? Math.max(0, Math.round((points[points.length - 1].at - points[0].at) / 86400000))
    : 0;

  return (
    <div className="mt-2 text-[11px] text-ink-muted space-y-1" data-testid="price-history-coverage">
      <div>
        {checks} price check{checks === 1 ? "" : "s"}
        {checks >= 2
          ? ` over ${spanDays} day${spanDays === 1 ? "" : "s"}`
          : ""}{" "}
        ·{" "}
        {checks === 1
          ? "A single observation is a check, not a trend. More observations are needed before any trend can be stated."
          : "Trend and averages use these observations only; gaps are not interpolated."}
      </div>
      {coverageMeta && coverageMeta.checks > 0 ? (
        <div data-testid="price-history-server-coverage">
          {coverageMeta.first_observed_at ? (
            <>
              First observed {fmtDay(new Date(coverageMeta.first_observed_at).getTime())}
              {coverageMeta.last_observed_at
                ? `, last observed ${fmtDay(new Date(coverageMeta.last_observed_at).getTime())}`
                : ""}
              .{" "}
            </>
          ) : null}
          {coverageMeta.checks} verified check
          {coverageMeta.checks === 1 ? "" : "s"}.
          {Array.isArray(coverageMeta.available_windows) &&
          coverageMeta.available_windows.length
            ? ` Averages available for ${coverageMeta.available_windows.join("/")}-day windows.`
            : " No averaging window is claimed: observations do not span one yet."}
        </div>
      ) : null}
      {observed.map((s) => {
        const st = stats[s.key];
        if (st.observations < 2) {
          return (
            <div key={s.key} data-testid={`price-history-single-${s.key}`}>
              {s.label}: one observation, so no change is calculated.
            </div>
          );
        }
        const dir = st.change > 0 ? "up" : st.change < 0 ? "down" : "unchanged";
        return (
          <div key={s.key} data-testid={`price-history-change-${s.key}`}>
            {s.label}: {money(st.first.values[s.key])} on {fmtTs(st.first.at)} →{" "}
            {money(st.last.values[s.key])} on {fmtTs(st.last.at)} ({dir}
            {st.pct != null ? ` ${Math.abs(st.pct).toFixed(1)}%` : ""})
          </div>
        );
      })}
    </div>
  );
}

export default PriceHistoryChart;
